import type { LogoForm } from '@sites/entity-signal-form';

import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { URL as URL_REGEX } from '@common/regexp';

import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';

/**
 * Alt/link sub-editor for the `logo` widget — the trimmed twin of the image
 * widget's `web-image-settings` (no overlay, effect, mask or text section; the
 * logo never had them). Fully signal-based: it receives the widget's
 * {@link LogoForm} and writes straight through to the picked file, so edits are
 * live — no detached FormGroup, no DTO round-trip.
 *
 * The parent (`web-logo-control`) opens this panel after snapshotting the current
 * picked file. `saveEvent` keeps the live edits and closes; `cancelEvent` asks the
 * parent to restore that snapshot.
 */
@Component({
    selector: 'web-image-settings',
    templateUrl: './image-settings.component.html',
    styleUrls: ['./image-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [WebSettingsModule, TranslateModule],
})
export class ImageSettingsComponent {
    public readonly form = input.required<LogoForm>();

    /** Keep the live edits and close the panel (footer "Save" + breadcrumb back). */
    public readonly saveEvent = output<void>();
    /** Revert to the snapshot taken when the panel opened, then close (footer "Cancel"). */
    public readonly cancelEvent = output<void>();

    /** Set on link blur — the validation hint only shows after the field is touched. */
    public readonly linkTouched = signal<boolean>(false);

    /**
     * Read through the whole picked file rather than per-leaf FieldTree children:
     * `data.image` is nullable on the schema, so those children do not exist while
     * no file is picked. Writes go through the form's `updateImage` patch for the
     * same reason.
     */
    public readonly alt = computed<string>(() => this.form().image().value()?.alt ?? '');
    public readonly link = computed<string>(() => this.form().image().value()?.link ?? '');

    /**
     * Presentational only — an invalid link was never blocked from saving, so
     * there is no schema-level validation behind it, exactly as before.
     */
    public readonly linkInvalid = computed<boolean>(() => {
        const link = this.link();
        return Boolean(link) && !new RegExp(URL_REGEX).test(link);
    });

    public setAlt(value: string): void {
        this.form().updateImage({ alt: value });
    }

    public setLink(value: string): void {
        this.form().updateImage({ link: value });
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }
}
